import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Boxes, Clock3, PackageCheck, Scale, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { EmptyState, ErrorState, LoadingState, StatusBadge } from "../components/AsyncState";
import { PageHeader } from "../components/PageHeader";
import { formatNumber } from "../lib/format";

type Dashboard = {
  metrics: {
    households: number;
    activeHouseholds: number;
    neverDelivered: number;
    averageWaitDays: number;
    batches: number;
    cylinders: number;
    currentBatches: number;
  };
  currentBatches: Array<{
    id: string;
    batch_number: string;
    quantity: number;
    status: string;
    agent_name: string;
    delivered: number;
    remaining: number;
  }>;
};

export function DashboardPage() {
  const query = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api<Dashboard>("/dashboard")).data
  });
  if (query.isPending) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} retry={() => query.refetch()} />;
  const { metrics, currentBatches } = query.data;
  const cards = [
    {
      label: "إجمالي الأسر",
      value: metrics.households,
      detail: `${formatNumber(metrics.activeHouseholds)} أسرة نشطة`,
      icon: Users,
      tone: "teal"
    },
    {
      label: "لم تستلم سابقًا",
      value: metrics.neverDelivered,
      detail: "أولوية في الدورة التأسيسية",
      icon: Scale,
      tone: "amber"
    },
    {
      label: "متوسط الانتظار",
      value: metrics.averageWaitDays,
      detail: "يومًا بين مرات الاستلام",
      icon: Clock3,
      tone: "blue"
    },
    {
      label: "إجمالي الأسطوانات",
      value: metrics.cylinders,
      detail: `${formatNumber(metrics.batches)} دفعة مسجلة`,
      icon: Boxes,
      tone: "violet"
    }
  ];
  return (
    <div className="page">
      <PageHeader
        title="لوحة المتابعة"
        description="صورة مباشرة من قاعدة البيانات ضمن نطاق صلاحيتك."
        actions={
          <Link className="button" to="/delivery">
            <PackageCheck aria-hidden="true" />
            فتح شاشة التسليم
          </Link>
        }
      />
      <section className="metric-grid" aria-label="المؤشرات الرئيسية">
        {cards.map(({ label, value, detail, icon: Icon, tone }) => (
          <article className={`metric-card metric-card--${tone}`} key={label}>
            <div className="metric-icon">
              <Icon aria-hidden="true" />
            </div>
            <div>
              <span>{label}</span>
              <strong>{formatNumber(value)}</strong>
              <small>{detail}</small>
            </div>
          </article>
        ))}
      </section>
      <section className="content-card">
        <div className="section-head">
          <div>
            <h2>الدفعات الجارية</h2>
            <p>{formatNumber(metrics.currentBatches)} دفعة جاهزة أو قيد التوزيع</p>
          </div>
          <Link className="text-link" to="/batches">
            عرض الكل <ArrowLeft aria-hidden="true" />
          </Link>
        </div>
        {currentBatches.length === 0 ? (
          <EmptyState title="لا توجد دفعات جارية" description="ستظهر الدفعات هنا بعد تخصيصها واعتمادها." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الدفعة</th>
                  <th>الوكيل</th>
                  <th>الحالة</th>
                  <th>التقدم</th>
                  <th>المتبقي</th>
                </tr>
              </thead>
              <tbody>
                {currentBatches.map((batch) => {
                  const percent =
                    batch.quantity > 0 ? Math.round((batch.delivered / batch.quantity) * 100) : 0;
                  return (
                    <tr key={batch.id}>
                      <td>
                        <Link to={`/batches/${batch.id}`} className="row-title">
                          {batch.batch_number}
                        </Link>
                      </td>
                      <td>{batch.agent_name}</td>
                      <td>
                        <StatusBadge status={batch.status} />
                      </td>
                      <td>
                        <div className="progress-label">
                          <span>
                            {formatNumber(batch.delivered)} من {formatNumber(batch.quantity)}
                          </span>
                          <span>{formatNumber(percent)}٪</span>
                        </div>
                        <progress max={batch.quantity} value={batch.delivered}>
                          {percent}%
                        </progress>
                      </td>
                      <td>{formatNumber(batch.remaining)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
